/**
 * Generate panel (spec §3: procedural-first, seed-rolled): discipline
 * selector (fire alarm / HVAC ductwork), HVAC settings popover (healthcare
 * toggle with auto-suggest hint, plenum depth), visible/copyable
 * "Variant #seed" with re-roll + pin, Generate button, and live job progress
 * with engine stage labels.
 *
 * Pure component — generation state lives in the MEP store; this renders
 * props and raises callbacks (only the popover open/closed flag is local).
 */
import { useState } from "react";
import type { CSSProperties } from "react";
import type { ElectricalSettings, HvacSettings, MepDiscipline } from "../api/types";
import { stageLabel, type GenerateState } from "../state/generateFlow";

export interface GeneratePanelProps {
  discipline: MepDiscipline;
  onDisciplineChange: (d: MepDiscipline) => void;
  flow: GenerateState;
  /** Seed of the last/active variant (from the flow or the loaded system). */
  seed: number | null;
  pinned: boolean;
  onTogglePin: () => void;
  /** Roll a fresh variant (unpin + generate). */
  onReroll: () => void;
  onGenerate: () => void;
  /** Generation gate from the readiness report. */
  enabled: boolean;
  blockedReason: string | null;
  /** HVAC generation settings (sent with the generate request). */
  hvacSettings?: HvacSettings;
  onHvacSettingsChange?: (patch: HvacSettings) => void;
  /** Healthcare-type spaces detected → suggest the ASHRAE 170 rule set. */
  healthcareSuggested?: boolean;
  /** Electrical generation settings (sent with the generate request). */
  electricalSettings?: ElectricalSettings;
  onElectricalSettingsChange?: (patch: ElectricalSettings) => void;
}

const buttonStyle: CSSProperties = {
  padding: "0.45rem 0.9rem",
  borderRadius: 6,
  border: "1px solid #4a90d9",
  background: "#4a90d9",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 600,
};

const ghostButton: CSSProperties = {
  ...buttonStyle,
  background: "transparent",
  color: "#4a90d9",
  fontWeight: 400,
};

export function GeneratePanel({
  discipline,
  onDisciplineChange,
  flow,
  seed,
  pinned,
  onTogglePin,
  onReroll,
  onGenerate,
  enabled,
  blockedReason,
  hvacSettings = {},
  onHvacSettingsChange,
  healthcareSuggested = false,
  electricalSettings = {},
  onElectricalSettingsChange,
}: GeneratePanelProps) {
  const busy = flow.phase === "submitting" || flow.phase === "running";
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <section
      data-testid="generate-panel"
      style={{
        background: "#21213a",
        borderRadius: 8,
        padding: "0.9rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.6rem",
      }}
    >
      {discipline === "hvac" && (
        <div
          data-testid="hvac-heading"
          style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}
        >
          <h2 style={{ margin: 0, fontSize: "1rem" }}>Supply Air &amp; Equipment</h2>
          <p style={{ margin: 0, color: "#8e8eb8", fontSize: "0.8rem" }}>
            Generates ductwork AND the equipment schedule (RTU/AHU/VAV/pump) in one run.
            Estimating-grade capacities from ASHRAE Handbook&#8212;Fundamentals screening methods,
            not equipment selection. The ductwork stage&#39;s own AHU is a screening placeholder.
            Return and exhaust ductwork are not generated yet.
          </p>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <span style={{ color: "#8e8eb8", fontSize: "0.85rem" }}>Discipline</span>
          <select
            data-testid="discipline-select"
            value={discipline}
            onChange={(e) => onDisciplineChange(e.target.value as MepDiscipline)}
            disabled={busy}
            style={{
              background: "#12121e",
              color: "#d8d8e8",
              border: "1px solid #555577",
              borderRadius: 6,
              padding: "0.35rem 0.5rem",
            }}
          >
            <option value="fire_alarm">Fire alarm</option>
            <option value="hvac">HVAC (ductwork + equipment)</option>
            <option value="electrical">Electrical (commercial)</option>
            <option value="structural">Structural (placement + screening)</option>
          </select>
        </label>

        {discipline === "hvac" && (
          <button
            type="button"
            data-testid="hvac-settings-toggle"
            onClick={() => setSettingsOpen((open) => !open)}
            disabled={busy}
            style={ghostButton}
          >
            HVAC settings {settingsOpen ? "▴" : "▾"}
          </button>
        )}

        {discipline === "electrical" && (
          <button
            type="button"
            data-testid="electrical-settings-toggle"
            onClick={() => setSettingsOpen((open) => !open)}
            disabled={busy}
            style={ghostButton}
          >
            Service settings {settingsOpen ? "▴" : "▾"}
          </button>
        )}

        {seed !== null && (
          <span
            data-testid="variant-seed"
            title="Procedural seed — re-running with this seed reproduces the variant exactly"
            style={{
              fontFamily: "monospace",
              background: "#12121e",
              borderRadius: 6,
              padding: "0.3rem 0.6rem",
              userSelect: "all",
            }}
          >
            Variant #{seed}
          </span>
        )}

        {seed !== null && (
          <label
            data-testid="seed-pin"
            style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.85rem" }}
          >
            <input type="checkbox" checked={pinned} onChange={onTogglePin} disabled={busy} />
            Pin seed
          </label>
        )}

        <button
          type="button"
          data-testid="reroll-button"
          onClick={onReroll}
          disabled={busy || !enabled}
          style={ghostButton}
          title="Roll a fresh seed and generate a new variant"
        >
          Re-roll
        </button>

        <button
          type="button"
          data-testid="generate-button"
          onClick={onGenerate}
          disabled={busy || !enabled}
          style={{
            ...buttonStyle,
            background: busy || !enabled ? "#2a2a44" : "#4a90d9",
            cursor: busy || !enabled ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "Generating…" : "Generate"}
        </button>
      </div>

      {discipline === "hvac" && settingsOpen && (
        <div
          data-testid="hvac-settings"
          style={{
            background: "#12121e",
            borderRadius: 8,
            padding: "0.7rem 0.8rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            fontSize: "0.85rem",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <input
              type="checkbox"
              data-testid="healthcare-toggle"
              checked={hvacSettings.healthcare ?? false}
              onChange={(e) => onHvacSettingsChange?.({ healthcare: e.target.checked })}
              disabled={busy}
            />
            Healthcare ventilation (ASHRAE 170 air-change rules)
          </label>
          {healthcareSuggested && !(hvacSettings.healthcare ?? false) && (
            <p
              data-testid="healthcare-hint"
              style={{ margin: 0, color: "#e5c07b", fontSize: "0.8rem" }}
            >
              Healthcare spaces (patient/operating rooms) detected in this model — the ASHRAE 170
              rule set is recommended.
            </p>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <span style={{ color: "#8e8eb8" }}>Plenum depth (m)</span>
            <input
              type="number"
              data-testid="plenum-depth-input"
              min={0.1}
              max={3}
              step={0.05}
              value={hvacSettings.plenum_depth_m ?? ""}
              placeholder="server default"
              onChange={(e) => {
                const value = e.target.valueAsNumber;
                onHvacSettingsChange?.({
                  plenum_depth_m: Number.isFinite(value) ? value : undefined,
                });
              }}
              disabled={busy}
              style={{
                width: 110,
                background: "#21213a",
                color: "#d8d8e8",
                border: "1px solid #555577",
                borderRadius: 6,
                padding: "0.25rem 0.4rem",
              }}
            />
          </label>
        </div>
      )}

      {discipline === "electrical" && settingsOpen && (
        <div
          data-testid="electrical-settings"
          style={{
            background: "#12121e",
            borderRadius: 8,
            padding: "0.7rem 0.8rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            fontSize: "0.85rem",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <span style={{ color: "#8e8eb8", width: 130 }}>Service voltage (V)</span>
            <select
              data-testid="service-voltage-select"
              value={electricalSettings.service_voltage ?? 208}
              onChange={(e) =>
                onElectricalSettingsChange?.({ service_voltage: Number(e.target.value) })
              }
              disabled={busy}
              style={{
                background: "#21213a",
                color: "#d8d8e8",
                border: "1px solid #555577",
                borderRadius: 6,
                padding: "0.25rem 0.4rem",
              }}
            >
              <option value={208}>208Y/120 V</option>
              <option value={480}>480Y/277 V</option>
              <option value={240}>240 V</option>
            </select>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <span style={{ color: "#8e8eb8", width: 130 }}>Phase</span>
            <select
              data-testid="service-phase-select"
              value={electricalSettings.service_phase ?? 3}
              onChange={(e) =>
                onElectricalSettingsChange?.({ service_phase: Number(e.target.value) })
              }
              disabled={busy}
              style={{
                background: "#21213a",
                color: "#d8d8e8",
                border: "1px solid #555577",
                borderRadius: 6,
                padding: "0.25rem 0.4rem",
              }}
            >
              <option value={3}>3-phase</option>
              <option value={1}>Single-phase</option>
            </select>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <span style={{ color: "#8e8eb8", width: 130 }}>Service ampacity (A)</span>
            <input
              type="number"
              data-testid="service-ampacity-input"
              min={15}
              max={6000}
              step={5}
              value={electricalSettings.service_ampacity ?? ""}
              placeholder="auto-size to demand"
              onChange={(e) => {
                const value = e.target.valueAsNumber;
                onElectricalSettingsChange?.({
                  service_ampacity: Number.isFinite(value) ? value : undefined,
                });
              }}
              disabled={busy}
              style={{
                width: 150,
                background: "#21213a",
                color: "#d8d8e8",
                border: "1px solid #555577",
                borderRadius: 6,
                padding: "0.25rem 0.4rem",
              }}
            />
          </label>
          <p style={{ margin: 0, color: "#8e8eb8", fontSize: "0.8rem" }}>
            Leave ampacity blank to auto-size the service to the computed NEC Article 220 demand.
            Pin a value to check a specific main frame — an undersized choice is flagged.
          </p>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <input
              type="checkbox"
              data-testid="ground-system-toggle"
              checked={electricalSettings.ground_system ?? false}
              onChange={(e) => onElectricalSettingsChange?.({ ground_system: e.target.checked })}
              disabled={busy}
            />
            Grounding &amp; bonding (NEC Article 250: GEC / bonding jumpers + electrode system)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <input
              type="checkbox"
              data-testid="emergency-power-toggle"
              checked={electricalSettings.emergency_power ?? false}
              onChange={(e) => onElectricalSettingsChange?.({ emergency_power: e.target.checked })}
              disabled={busy}
            />
            Emergency &amp; standby power (NEC Article 517/700: source + ATS + EES branch
            classification)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <input
              type="checkbox"
              data-testid="voltage-drop-toggle"
              checked={electricalSettings.voltage_drop_analysis ?? false}
              onChange={(e) =>
                onElectricalSettingsChange?.({ voltage_drop_analysis: e.target.checked })
              }
              disabled={busy}
            />
            Voltage-drop analysis (NEC 210.19/215.2 IN: branch 3% / combined 5%)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <input
              type="checkbox"
              data-testid="lighting-controls-toggle"
              checked={electricalSettings.lighting_controls ?? false}
              onChange={(e) =>
                onElectricalSettingsChange?.({ lighting_controls: e.target.checked })
              }
              disabled={busy}
            />
            Lighting controls (Title 24 §130.1(b): multi-level control ≥100 sf &amp; &gt;0.5 W/sf)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <input
              type="checkbox"
              data-testid="egress-illumination-toggle"
              checked={electricalSettings.egress_illumination ?? false}
              onChange={(e) =>
                onElectricalSettingsChange?.({ egress_illumination: e.target.checked })
              }
              disabled={busy}
            />
            Egress illumination (NFPA 101 §7.9 / IBC §1008: emergency path ≥1 fc)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <input
              type="checkbox"
              data-testid="demand-response-toggle"
              checked={electricalSettings.demand_response_controls ?? false}
              onChange={(e) =>
                onElectricalSettingsChange?.({ demand_response_controls: e.target.checked })
              }
              disabled={busy}
            />
            Demand-responsive controls (Title 24 §130.1(e): ≥4,000 W lighting)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <input
              type="checkbox"
              data-testid="motor-circuits-toggle"
              checked={electricalSettings.motor_circuits ?? false}
              onChange={(e) => onElectricalSettingsChange?.({ motor_circuits: e.target.checked })}
              disabled={busy}
            />
            Motor circuits (NEC 430/440: conductor/OCPD/overload sizing)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <input
              type="checkbox"
              data-testid="daylighting-controls-toggle"
              checked={electricalSettings.daylighting_controls ?? false}
              onChange={(e) =>
                onElectricalSettingsChange?.({ daylighting_controls: e.target.checked })
              }
              disabled={busy}
            />
            Daylighting controls (Title 24 §130.1(d): sidelit/skylit zones)
          </label>
        </div>
      )}

      {!enabled && blockedReason && (
        <p
          data-testid="generate-blocked"
          style={{ margin: 0, color: "#e5c07b", fontSize: "0.85rem" }}
        >
          Generation disabled: {blockedReason}
        </p>
      )}

      {flow.phase === "running" && (
        <div data-testid="generate-progress">
          <div style={{ marginBottom: "0.3rem", fontSize: "0.85rem" }}>
            {stageLabel(flow.stage)} ({flow.progress}%)
          </div>
          <div style={{ height: 6, borderRadius: 3, background: "#2a2a44", overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${flow.progress}%`,
                background: "#4a90d9",
                transition: "width 120ms linear",
              }}
            />
          </div>
        </div>
      )}

      {flow.phase === "failed" && (
        <p data-testid="generate-error" style={{ margin: 0, color: "#e06c75" }}>
          Generation failed: {flow.message}
        </p>
      )}
    </section>
  );
}
